// @ts-strict-ignore
import invert from 'lodash/invert';

import {userDetailsStore} from 'common/report/stores/user-details';
import {HeaderUserInfo} from 'common/widgets/user-list/types';
import {toTitleCase} from 'common/util/string';
import {promisifyModal} from 'common/components/modal/utils';
import {confirm} from 'common/components/confirm';
import {emitTrackEvent} from 'common/util/dom';
import {SearchDisplayContext, SearchResultType} from 'common/report/util/search/types';
import {ProjectDetails} from 'common/report/stores/project-details';
import {FeatureGate, Permission} from 'common/report/constants';
import {GlobalAccessType} from 'common/widgets/create-entity-modal/types';
import {SharingAccessType} from 'common/widgets/sharing/types';
import {CurrentProject} from 'common/report/model-resources/projects';
import {Bookmark, Dashboard} from 'common/report/dashboards/types';
import {Translatable, TranslationKey} from 'common/i18n';
import {LocalizationStore} from 'common/i18n/store';
import {Canvas} from 'apps/metric-tree/src/types';
import {Creator} from 'common/types/globals/creator';
import {Experiment} from 'apps/experiments/src/types';
import {FeatureFlagApiResponse} from 'app_api/projects/feature_flags/types';
import {camelCaseKeys} from 'common/util';
import {Content} from 'common/types/app-api/content';

import {getProjectId} from '../index';
import {DashboardOrigin} from '../bookmark-overrides';
import {
  CustomProperty,
  Entity,
  EntityType,
  EntityTypeKey,
  EntityTypeNamePlural,
  EntityTypePermissions,
  GlobalAccessParamName,
  OverrideModal,
  OwnerFilters,
  SharableEntityType,
  SubEntityType,
  UserRole,
} from './types';

// TODO evnp - add JSDoc comments to all these utils when there's bandwidth

export const OwnerFiltersI18nKey: Record<OwnerFilters, TranslationKey> = {
  [OwnerFilters.All]: `components.sharing.owner-filter.all`,
  [OwnerFilters.Yours]: `components.sharing.owner-filter.yours`,
  [OwnerFilters.NotYours]: `components.sharing.owner-filter.not-yours`,
  [OwnerFilters.Shared]: `components.sharing.owner-filter.shared`,
};

const EntityTypeNameI18nKey = {
  [EntityType.Report]: `mixology.report`,
  [EntityType.Dashboard]: `mixology.board`,
  [EntityType.Cohort]: `mixology.cohort`,
  [EntityType.CustomEvent]: `mixology.custom-event`,
  [EntityType.CustomProperty]: `mixology.custom-property`,
  [EntityType.Event]: `mixology.event`,
  [EntityType.Property]: `mixology.property`,
  [EntityType.LookupTable]: `mixology.lookup-table`,
  [EntityType.DataGroup]: `mixology.data-group`,
  [EntityType.Journey]: `mixology.journey`,
  [EntityType.Workspace]: `mixology.workspace`,
  [EntityType.Metric]: `mixology.metric`,
  [EntityType.Behavior]: `mixology.behavior`,
  [EntityType.MetricTree]: `mixology.metric-tree`,
  [EntityType.Experiment]: `mixology.experiment`,
  [EntityType.FeatureFlag]: `mixology.feature-flag`,
} as const satisfies Record<EntityType, TranslationKey>;

const ENTITY_TYPE_WITH_CONTENT_SUPPORT = [
  EntityType.Dashboard,
  EntityType.Experiment,
  EntityType.FeatureFlag,
  EntityType.MetricTree,
  EntityType.Report,
] as const;

export type EntityTypeWithContentSupport = (typeof ENTITY_TYPE_WITH_CONTENT_SUPPORT)[number];

export function getEntityTypeName(
  entityType: EntityType,
  {
    plural = false,
    titleCased = false, // every word capitalized
  }: {
    plural?: boolean;
    titleCased?: boolean;
  } = {},
): Translatable {
  const i18nKey = EntityTypeNameI18nKey[entityType];
  if (!i18nKey) {
    // shouldn't happen, but just in case...
    return entityType;
  }

  return ({t, language}: LocalizationStore) => {
    let entityTypeName = t(i18nKey, {
      ...(plural && {context: `plural`}),
    });

    if (titleCased) {
      entityTypeName = toTitleCase(entityTypeName);
    } else {
      entityTypeName = entityTypeName.toLocaleLowerCase(language);
    }

    return entityTypeName;
  };
}

export function isSharingEnabledForProject(project?: ProjectDetails | CurrentProject | null) {
  return project?.getFeatureGate(FeatureGate.Sharing);
}

export function isUserProjectOwner(project: ProjectDetails | CurrentProject | null) {
  return project?.hasPermission(Permission.EditLockedEntity);
}

export function getOwnerFilterOptions({
  project,
  includeAll,
}: {
  project: ProjectDetails | CurrentProject | null;
  includeAll?: boolean;
}): OwnerFilters[] {
  const includeAllFilter =
    !isSharingEnabledForProject(project) ||
    isUserProjectOwner(project) ||
    (project && allowStaffOverride(project.id)) ||
    includeAll;
  return [
    ...(includeAllFilter ? [OwnerFilters.All] : []),
    OwnerFilters.Yours,
    ...(isSharingEnabledForProject(project) ? [OwnerFilters.Shared] : [OwnerFilters.NotYours]),
  ];
}

const ENTITY_TYPE_TO_PERMISSION: Partial<{[k in EntityType]: Permission}> = {
  [EntityType.Report]: Permission.WriteTypedBookmarks,
  [EntityType.Dashboard]: Permission.WriteDashboards,
  [EntityType.MetricTree]: Permission.WriteMetricTrees,
  [EntityType.Cohort]: Permission.WriteCohorts,
  [EntityType.CustomEvent]: Permission.WriteCustomEvents,
  [EntityType.CustomProperty]: Permission.WriteCustomProperties,
  [EntityType.Metric]: Permission.WriteMetrics,
  [EntityType.LookupTable]: Permission.WriteLookupTables,
  [EntityType.Experiment]: Permission.WriteExperiments,
  [EntityType.FeatureFlag]: Permission.WriteFeatureFlags,
};

const ENTITY_TYPE_TO_SHARABLE_ENTITY_TYPE: Partial<{[k in EntityType]: SharableEntityType}> = {
  [EntityType.Report]: SharableEntityType.Report,
  [EntityType.Dashboard]: SharableEntityType.Dashboard,
  [EntityType.MetricTree]: SharableEntityType.MetricTree,
  [EntityType.Experiment]: SharableEntityType.Experiment,
  [EntityType.FeatureFlag]: SharableEntityType.FeatureFlag,
  [EntityType.Cohort]: SharableEntityType.Cohort,
  [EntityType.CustomProperty]: SharableEntityType.CustomProperty,
  [EntityType.Metric]: SharableEntityType.Metric,
  [EntityType.CustomEvent]: SharableEntityType.CustomEvent,
  [EntityType.LookupTable]: SharableEntityType.LookupTable,
  [EntityType.Behavior]: SharableEntityType.Behavior,
};

const ENTITY_TYPE_TO_SEARCH_RESULT_TYPE: Partial<{[k in EntityType]: SearchResultType}> = {
  [EntityType.Cohort]: SearchResultType.Cohort,
  [EntityType.Dashboard]: SearchResultType.Dashboard,
  [EntityType.CustomEvent]: SearchResultType.Event,
  [EntityType.MetricTree]: SearchResultType.MetricTree,
  [EntityType.Report]: SearchResultType.Report,
  [EntityType.Experiment]: SearchResultType.Experiment,
  [EntityType.FeatureFlag]: SearchResultType.FeatureFlag,
};

const SHARABLE_ENTITY_TYPE_TO_ENTITY_TYPE = invert(ENTITY_TYPE_TO_SHARABLE_ENTITY_TYPE) as Partial<{
  [k in SharableEntityType]: EntityType;
}>;
const SEARCH_RESULT_TYPE_TO_ENTITY_TYPE = invert(ENTITY_TYPE_TO_SEARCH_RESULT_TYPE) as Partial<{
  [k in SearchResultType]: EntityType;
}>;

export function getEntityTypePermission(entityType: EntityType): Permission {
  return ENTITY_TYPE_TO_PERMISSION[entityType];
}

export function getEntityType(sharableOrSearchType: SharableEntityType | SearchResultType): EntityType {
  const entityType =
    SHARABLE_ENTITY_TYPE_TO_ENTITY_TYPE[sharableOrSearchType] ??
    SEARCH_RESULT_TYPE_TO_ENTITY_TYPE[sharableOrSearchType];
  if (!entityType) {
    throw new Error(`Entity type ${sharableOrSearchType} does not map to an entity.`);
  }

  return entityType;
}

export function getEntityTypeWithContentSupport(sharableOrSearchType: SharableEntityType | SearchResultType) {
  const entityType = getEntityType(sharableOrSearchType) as EntityTypeWithContentSupport;
  if (!ENTITY_TYPE_WITH_CONTENT_SUPPORT.includes(entityType)) {
    throw new Error(`Entity type ${entityType} is not yet implemented in the content ecosystem.`);
  }

  return entityType;
}

export function getSharableEntityType(entityType: EntityType | SearchResultType): SharableEntityType {
  const derivedEntityType = SEARCH_RESULT_TYPE_TO_ENTITY_TYPE[entityType] ?? entityType;
  const sharableEntityType = ENTITY_TYPE_TO_SHARABLE_ENTITY_TYPE[derivedEntityType];

  if (!sharableEntityType) {
    throw new Error(`Entity type ${entityType} is not shareable.`);
  }

  return sharableEntityType;
}

export function getSearchResultEntityType(entityType: EntityType | SharableEntityType): SearchResultType {
  const derivedEntityType = SHARABLE_ENTITY_TYPE_TO_ENTITY_TYPE[entityType] ?? entityType;
  const searchResultEntityType = ENTITY_TYPE_TO_SEARCH_RESULT_TYPE[derivedEntityType];

  if (!searchResultEntityType) {
    throw new Error(`Entity type ${entityType} is not searchable.`);
  }

  return searchResultEntityType;
}

export function isSharableEntityType(entityType: EntityType): boolean {
  return Object.keys(ENTITY_TYPE_TO_SHARABLE_ENTITY_TYPE).includes(entityType);
}

// TODO support passing in a ProjectDetails model so that we don't rely on UserDetailsStore for
// project permissions/roles
export function getEntityTypePermissions(projectId: number, entityType: EntityType): EntityTypePermissions {
  const project = userDetailsStore.value.projects?.[projectId];
  // project/org permissions may be sets; convert to array for compatibility
  const permissions = Array.from(project?.permissions || []);
  const role = (project?.role?.name as UserRole) || UserRole.Consumer; // default to Consumer role; minimal permissions

  // staff users can do anything in projects they are not a member of
  if (!project && userDetailsStore.value.isStaff) {
    return {
      role: UserRole.Admin,
      can_update_basic: true,
      can_share: true,
    };
  }

  return {
    role,
    can_update_basic: permissions.includes(getEntityTypePermission(entityType)),
    can_share: permissions.includes(Permission.EditCreatedEntityVisibility),
  };
}

type BuildCreateEntityModalAttrsArgs = {
  projectId: number;
  entityType: EntityType;
  subEntityType?: SubEntityType;
  entity: Entity | null;
  modalOpen: boolean;
  modalCloseable?: boolean;
  isEditing: boolean;
  isDuplicating: boolean;
  editWarning?: string;
  modalTitleOverride?: string;
  showShareManagement?: boolean;
  showCopyLinkButton?: boolean;
  i18n: LocalizationStore;
};

// TODO opslead
export function buildCreateEntityModalAttrs({
  projectId,
  entityType,
  subEntityType,
  entity,
  modalOpen,
  modalCloseable,
  isEditing,
  isDuplicating,
  editWarning,
  modalTitleOverride,
  showShareManagement,
  showCopyLinkButton,
  i18n,
}: BuildCreateEntityModalAttrsArgs) {
  const entityTypePermissions = getEntityTypePermissions(projectId, entityType);
  const isConsumer = entityTypePermissions.role === UserRole.Consumer;
  const visibilityControlsMessage =
    isConsumer &&
    `Due to your project role (Consumer), your ${i18n.print(
      getEntityTypeName(entityType, {
        plural: true,
      }),
    )} are always hidden and not editable by others`;

  if (entity && isDuplicating && !entity.name.endsWith(`(copy)`)) {
    // append "(copy)" onto name
    entity = {
      ...entity,
      name: `${entity.name} (copy)`,
    };
  }

  let modalTitle = i18n.t(`components.create-entity.create`);
  if (isDuplicating) {
    modalTitle = i18n.t(`components.create-entity.create`);
  } else if (isEditing) {
    modalTitle = i18n.t(`components.create-entity.edit`);
  }

  if (modalTitleOverride) {
    modalTitle = modalTitleOverride;
  }
  return {
    open: modalOpen,
    closeable: modalCloseable,
    'project-id': projectId,
    'modal-title': modalTitle,
    'entity-type': entityType,
    ...(subEntityType && {'sub-entity-type': subEntityType}),
    'is-editing': isEditing,
    'initial-name': (entity && entity.name) || ``,
    'initial-description': (entity && entity.description) || ``,
    warning: isEditing && editWarning ? editWarning : ``,
    'show-share-management': !isConsumer && showShareManagement,
    // if not passed in, defaults to false
    'show-copy-link-button': showCopyLinkButton ?? false,
    // sharing-disabled (visibility controls in create-entity-modal)
    'visibility-controls-message': visibilityControlsMessage || ``,
  };
}

export function buildCreateEntityModalProps(params: BuildCreateEntityModalAttrsArgs) {
  return camelCaseKeys(buildCreateEntityModalAttrs(params));
}

// TODO opslead
export function buildQueryReportHeaderParams({
  projectId,
  entityType = EntityType.Report,
  entity = null,
  invalidEntity = false,
  invalidMessage = null,
  displayContext = null,
  dashboardPermOverride = null,
  dashboardsRoute = [],
}: {
  projectId: number;
  entityType?: EntityType;
  entity: Entity | null;
  invalidEntity?: boolean;
  invalidMessage?: string;
  displayContext: SearchDisplayContext;
  dashboardPermOverride?: Entity | null; // specifically dashboard entity
  dashboardsRoute?: DashboardOrigin[];
}): {
  entityType: EntityType;
  projectId: number;
  selectedEntityId: number | null;
  selectedEntityCreator: HeaderUserInfo;
  selectedEntityEditor: HeaderUserInfo;
  selectedEntityName: string;
  selectedEntityDescription: string;
  invalidEntity: boolean;
  invalidMessage: string | null;
  displayContext: SearchDisplayContext;
  emptyEntityName: string;
  dashboardsRoute?: DashboardOrigin[];
  // permissions
  canWrite: boolean;
  canSave: boolean;
  canShare: boolean;
  isSuperadmin: boolean;
  allowStaffOverride: boolean;
} {
  const entityTypePermissions = getEntityTypePermissions(projectId, entityType);
  const isUnsaved = !(entity && entity.id);

  let canWrite = entityTypePermissions.can_update_basic;
  let canSave = !invalidEntity && (isUnsaved || entity.can_update_basic);
  let canShare = !isUnsaved && entity.can_share;
  let isSuperAdmin = entity?.is_superadmin;
  let canAllowStaffOverride = entity?.allow_staff_override;

  if (dashboardPermOverride) {
    canWrite = getEntityTypePermissions(projectId, EntityType.Dashboard).can_update_basic;
    canShare = dashboardPermOverride?.can_share;
    canSave = dashboardPermOverride?.can_update_basic;
    isSuperAdmin = dashboardPermOverride?.is_superadmin;
    canAllowStaffOverride = dashboardPermOverride?.allow_staff_override;
  }

  const selectedEntityCreator: HeaderUserInfo = entity?.userId && {
    id: entity.userId,
    name: entity.userName,
    email: entity.userEmail,
    date: entity.created,
  };

  const selectedEntityEditor: HeaderUserInfo = entity?.modified && {
    id: entity.lastModifiedUserId,
    name: entity.lastModifiedUserName,
    email: entity.lastModifiedUserEmail,
    date: entity.modified,
  };

  return {
    entityType,
    projectId,
    selectedEntityId: (entity && (entity.id as number)) || null,
    selectedEntityCreator,
    selectedEntityEditor,
    selectedEntityName: (entity && entity.name) || ``,
    selectedEntityDescription: (entity && entity.description) || ``,
    invalidEntity,
    invalidMessage,
    displayContext,
    emptyEntityName: `Untitled`,
    dashboardsRoute,

    // permissions
    canWrite,
    canSave,
    canShare,
    isSuperadmin: isSuperAdmin || entityTypePermissions.role === UserRole.Owner,
    allowStaffOverride: canAllowStaffOverride || allowStaffOverride(projectId),
  };
}

export function canEditEntity({
  entity,
  entityType,
  projectId,
}: {
  entity: Entity;
  entityType: EntityType;
  projectId: number;
}): boolean {
  const entityTypePermissions = getEntityTypePermissions(projectId, entityType);
  return (
    (entityTypePermissions.can_update_basic && entity.can_update_basic) ||
    entity.is_superadmin ||
    entity.allow_staff_override
  );
}

export function normalizeBookmarkEntity(bookmark: Bookmark): Entity | null {
  return !bookmark
    ? null
    : {
        created: bookmark.created,
        description: bookmark.description,
        id: bookmark.id,
        lastModifiedUserEmail: bookmark.last_modified_by_email,
        lastModifiedUserId: bookmark.last_modified_by_id,
        lastModifiedUserName: bookmark.last_modified_by_name,
        modified: bookmark.modified,
        name: bookmark.name,
        userEmail: bookmark.creator_email,
        userId: bookmark.creator_id,
        userName: bookmark.creator_name,

        // permissions
        can_view: !!bookmark.can_view,
        can_update_basic: !!bookmark.can_update_basic,
        can_share: !!bookmark.can_share,
        is_superadmin: !!bookmark.is_superadmin,
        allow_staff_override: !!bookmark.allow_staff_override,
      };
}

export function normalizeDashboardEntity(dashboard: Partial<Dashboard> | null): Entity | null {
  return !dashboard
    ? null
    : {
        created: dashboard.created,
        description: dashboard.description,
        id: dashboard.id,
        lastModifiedUserEmail: dashboard.last_modified_by_email,
        lastModifiedUserId: dashboard.last_modified_by_id,
        lastModifiedUserName: dashboard.last_modified_by_name,
        modified: dashboard.modified,
        name: dashboard.title,
        userEmail: dashboard.creator_email,
        userId: dashboard.creator_id,
        userName: dashboard.creator_name,

        // permissions
        can_view: !!dashboard.can_view,
        can_update_basic: !!dashboard.can_update_basic,
        can_share: !!dashboard.can_share,
        is_superadmin: !!dashboard.is_superadmin,
        allow_staff_override: !!dashboard.allow_staff_override,
      };
}

export function normalizeCanvasEntity(canvas: Canvas | null): Entity | null {
  return !canvas
    ? null
    : {
        created: canvas.created,
        description: canvas.description,
        id: canvas.id,
        lastModifiedUserEmail: canvas.last_modified_by_email,
        lastModifiedUserId: canvas.last_modified_by_id,
        lastModifiedUserName: canvas.last_modified_by_name,
        modified: canvas.modified,
        name: canvas.name,
        userEmail: canvas.creator_email,
        userId: canvas.creator_id,
        userName: canvas.creator_name,

        // permissions
        can_view: !!canvas.can_view,
        can_update_basic: !!canvas.can_update_basic,
        can_share: !!canvas.can_share,
        is_superadmin: !!canvas.is_superadmin,
        allow_staff_override: !!canvas.allow_staff_override,
      };
}

export function normalizeExperimentEntity(experiment?: Experiment): Entity | null {
  return !experiment
    ? null
    : {
        created: experiment.created,
        id: experiment.id,
        lastModifiedUserEmail: experiment.last_modified_by_email,
        lastModifiedUserId: experiment.last_modified_by_id,
        lastModifiedUserName: experiment.last_modified_by_name,
        modified: experiment.modified,
        name: experiment.name,
        userEmail: experiment.creator_email,
        userId: experiment.creator_id,
        userName: experiment.creator_name,

        // permissions
        can_view: !!experiment.can_view,
        can_update_basic: !!experiment.can_update_basic,
        can_share: !!experiment.can_share,
        is_superadmin: !!experiment.is_superadmin,
        allow_staff_override: !!experiment.allow_staff_override,
      };
}

export function normalizeFeatureFlagEntity(featureFlag?: FeatureFlagApiResponse): Entity | null {
  return !featureFlag
    ? null
    : {
        created: featureFlag.modified, // Assuming "modified" is the closest equivalent to "created"
        id: featureFlag.id,
        lastModifiedUserEmail: featureFlag.last_modified_by_email,
        lastModifiedUserId: featureFlag.last_modified_by_id,
        lastModifiedUserName: featureFlag.last_modified_by_name,
        modified: featureFlag.modified,
        name: featureFlag.name,
        userEmail: featureFlag.creator_email,
        userId: featureFlag.creator_id,
        userName: featureFlag.creator_name,

        // permissions
        can_view: !!featureFlag.can_view,
        can_update_basic: !!featureFlag.can_update_basic,
        can_share: !!featureFlag.can_share,
        is_superadmin: !!featureFlag.is_superadmin,
        allow_staff_override: !!featureFlag.allow_staff_override,
      };
}

export function normalizeContentEntity(content?: Content | null): Entity | null {
  return !content
    ? null
    : {
        created: content.created,
        description: content.description,
        id: content.id,
        lastModifiedUserEmail: content.last_modified_by_email,
        lastModifiedUserId: content.last_modified_by_id,
        lastModifiedUserName: content.last_modified_by_name,
        modified: content.modified,
        name: content.name,
        userEmail: content.creator_email,
        userId: content.creator_id,
        userName: content.creator_name,

        // permissions
        can_view: !!content.can_view,
        can_update_basic: !!content.can_update_basic,
        can_share: !!content.can_share,
        is_superadmin: !!content.is_superadmin,
        allow_staff_override: !!content.allow_staff_override,
      };
}

type NormalizableEntity = {
  id: number;
  name: string;
  description: string;
  can_view?: boolean;
  can_update_basic?: boolean;
  can_share?: boolean;
  is_superadmin?: boolean;
  allow_staff_override?: boolean;
};

type NormalizableSavedCohort =
  | (NormalizableEntity & {
      createdBy: Creator;
    })
  | (NormalizableEntity & {
      created_by: Creator;
    });
type NormalizableUnsavedCohort = Omit<NormalizableEntity, `id`>;
type NormalizableCohort = NormalizableSavedCohort | NormalizableUnsavedCohort;

export function normalizeCohortEntity(cohort: NormalizableCohort | null) {
  if (!cohort) {
    return null;
  }

  if (`id` in cohort) {
    const creator = `createdBy` in cohort ? cohort.createdBy : cohort.created_by;

    if (!creator) {
      throw new Error(`Cohort didn't have a creator`);
    }

    return {
      id: cohort.id,
      name: cohort.name,
      description: cohort.description,
      userId: creator.id,
      userName: creator.name,
      userEmail: creator.email,

      // permissions
      can_view: !!cohort.can_view,
      can_update_basic: !!cohort.can_update_basic,
      can_share: !!cohort.can_share,
      is_superadmin: !!cohort.is_superadmin,
      allow_staff_override: !!cohort.allow_staff_override,
    };
  }
  return {
    name: cohort.name,
    description: cohort.description,

    // permissions
    can_view: !!cohort.can_view,
    can_update_basic: !!cohort.can_update_basic,
    can_share: !!cohort.can_share,
    is_superadmin: !!cohort.is_superadmin,
    allow_staff_override: !!cohort.allow_staff_override,
  };
}

export function normalizeCustomPropertyEntity(customProperty: CustomProperty | null): Entity | null {
  return !customProperty
    ? null
    : {
        id: null, // only ever used for creating
        userId: customProperty.userId || customProperty.user, // TODO @evnp - eliminate "user" attr for user ID, super confusing
        userName: customProperty.userName,
        userEmail: customProperty.userEmail,
        name: customProperty.name,
        description: customProperty.description,

        // permissions
        can_view: !!customProperty.can_view,
        can_update_basic: !!customProperty.canUpdateBasic,
        can_share: !!customProperty.can_share,
        is_superadmin: !!customProperty.is_superadmin,
        allow_staff_override: !!customProperty.allow_staff_override,
      };
}

interface EntitySection {
  label: string;
  items: any[];
  loading: boolean;
}
/**
 * Creates an array for the query-report-header depending on share FF and owner
 * @param yourEntities
 * @param visibleEntities
 * @param allEntities
 * @param userIsOwner
 * @param canShare
 */
export function createEntitiesSectionsArray({
  yourEntities = {
    label: ``,
    items: [],
    loading: false,
  },
  visibleEntities = {
    label: ``,
    items: [],
    loading: false,
  },
  allEntities = {
    label: ``,
    items: [],
    loading: false,
  },
  userIsOwner = false,
  canShare = false,
}: {
  yourEntities: EntitySection;
  visibleEntities: EntitySection;
  allEntities: EntitySection;
  userIsOwner: boolean;
  canShare: boolean;
}): any[] {
  if (!canShare) {
    return [yourEntities, allEntities];
  } else if (userIsOwner) {
    return [yourEntities, visibleEntities, allEntities];
  }
  return [yourEntities, visibleEntities];
}

interface OverrideOptions {
  i18n: LocalizationStore;
  cancel?: boolean;
  isBulkAction?: boolean;
  selectedEntities?: Dashboard[];
  selectedSuperadminOverrideEntities?: Dashboard[];
}
export async function promptSuperadminOverrideSave(
  entity: EntityType,
  options: OverrideOptions,
): Promise<OverrideModal> {
  const {i18n} = options;
  const isStaff = allowStaffOverride(getProjectId());
  let outcome = OverrideModal.Closed;

  const isBulkAction = options.isBulkAction;
  const cancel = isBulkAction || options.cancel;
  let entityTypeName = i18n.print(getEntityTypeName(entity));
  let confirmLabel = `Yes, Add`;
  // TODO: Custom descriptions for different cases (e.g. there was text here for adding report to dashboard)
  let description = `The creator of this ${entityTypeName} has not given you edit access.\n\nAs a ${
    isStaff ? `staff user` : `project Owner`
  } you can override this setting. Would you like to continue?`;

  if (isBulkAction) {
    const entities = options.selectedEntities ? options.selectedEntities : [];
    const superadminEntities = options.selectedSuperadminOverrideEntities
      ? options.selectedSuperadminOverrideEntities
      : [];
    const entitiesCount = entities.length;
    const superadminEntitiesCount = superadminEntities.length;

    if (superadminEntitiesCount) {
      entityTypeName = i18n.print(getEntityTypeName(entity, {plural: superadminEntitiesCount > 1}));
      const hasNonOverrideEntities = superadminEntitiesCount !== entitiesCount;
      const additionalText = hasNonOverrideEntities ? `out of ${entitiesCount}` : ``;
      if (hasNonOverrideEntities) {
        confirmLabel = `Yes, Add to All`;
      }
      const superadminEntitiesNames = superadminEntities.map((dashboard) => `• ${dashboard.title}`).join(`\n`);
      description =
        `You have not been given edit access to the following ${superadminEntitiesCount} ${additionalText} ${entityTypeName}:\n\n` +
        superadminEntitiesNames +
        `\n\nAs a ${
          isStaff ? `staff user` : `project Owner`
        } you can override this setting. Would you like to continue?`;
    }
  }
  const {detail} = await promisifyModal({
    tagName: `mp-confirm`,
    attrs: {
      title: `You Don't Have Access`,
      description,
      'description-allow-newline': true,
      closeable: true,
      'cancel-button-label': cancel ? `Cancel` : `No, Make a Copy`,
      'confirm-button-label': isBulkAction ? confirmLabel : `Yes, Continue`,
      elref: `superAdminOverrideSave`,
    },
    closeEvents: [`submit`],
  });
  if (detail.action === `cancel`) {
    outcome = cancel ? OverrideModal.Closed : OverrideModal.Copy;
  } else if (detail.action === `confirm`) {
    outcome = OverrideModal.Save;
  }

  emitTrackEvent(window, {
    eventName: `[Override] Save`,
    props: {
      'Entity Type': entity,
      Outcome: outcome,
      'Staff Override': isStaff,
    },
  });

  return outcome;
}

export async function promptSuperadminOverrideShare(
  entity: EntityType,
  {i18n}: {i18n: LocalizationStore},
): Promise<boolean> {
  const isStaff = allowStaffOverride(getProjectId());
  const outcome = await confirm({
    title: `You Don't Have Access`,
    description: `The creator of this ${i18n.print(getEntityTypeName(entity))} has not given you edit access to share.\n\nAs a ${
      isStaff ? `staff user` : `project Owner`
    } you can override this setting. Would you like to continue?`,
    'description-allow-newline': true,
    closeable: true,
    'confirm-button-label': `Yes, Share`,
  });

  emitTrackEvent(window, {
    eventName: `[Override] Share`,
    props: {
      'Entity Type': entity,
      Outcome: outcome,
      'Staff Override': isStaff,
    },
  });

  return outcome;
}

const enum UserAccess {
  Owner = `owner`,
  Editor = `editor`,
  Viewer = `viewer`,
  None = `none`,
}

export function getUserAccess(entity, {ownerId, userId}: {ownerId: number; userId: number}): UserAccess {
  return ownerId === userId
    ? UserAccess.Owner
    : entity.can_update_basic || entity.canUpdateBasic
      ? UserAccess.Editor
      : entity.can_view || entity.canView
        ? UserAccess.Viewer
        : UserAccess.None;
}

export function getUserAccessLabel(userAccess: UserAccess): Translatable {
  return ({t}) => t(`lexicon.access.${userAccess}`);
}

interface Capability {
  requireSuperadminOverride: boolean;
}

interface EditCapability extends Capability {
  enableEdit: boolean;
  disabledEditReason: string;
}

interface ShareCapability extends Capability {
  enableShare: boolean;
  disabledShareReason: string;
  sharingAccessType: SharingAccessType;
}

/** Is the user allowed to override standard grant permissions */
export function isAllowedSuperadminOverride(entity): boolean {
  return entity
    ? Boolean(
        entity.allowStaffOverride ||
          entity.allow_staff_override ||
          ((entity.isSuperadmin || entity.is_superadmin) && !(entity.canUpdateBasic || entity.can_update_basic)),
      )
    : false;
}

// Determine if a user has edit capability for a given entity
export function getEditCapability(entity): EditCapability {
  if (!entity) {
    return {
      enableEdit: false,
      requireSuperadminOverride: false,
      disabledEditReason: ``,
    };
  }

  // User is not given an editor access to share, but has superadmin or staff privilege
  if (isAllowedSuperadminOverride(entity)) {
    return {
      enableEdit: true,
      requireSuperadminOverride: true,
      disabledEditReason: ``,
    };
  }

  if (!(entity.canUpdateBasic || entity.can_update_basic)) {
    return {
      enableEdit: false,
      requireSuperadminOverride: false,
      disabledEditReason: `You have view-only access`,
    };
  }

  return {
    enableEdit: true,
    requireSuperadminOverride: false,
    disabledEditReason: ``,
  };
}

// Determine if a user has share capability for a given entity
export function getShareCapability(
  entity,
  entityType: EntityType = EntityType.Report,
  opts: {isSharingEnabled: boolean} = {isSharingEnabled: true},
): ShareCapability {
  // For projects that do not have the sharing feature gate, we enable share button
  // but show the upsell banner in share modal
  if (!opts.isSharingEnabled) {
    return {
      enableShare: true,
      requireSuperadminOverride: false,
      disabledShareReason: ``,
      sharingAccessType: SharingAccessType.None,
    };
  }

  if (!entity) {
    return {
      enableShare: false,
      requireSuperadminOverride: false,
      disabledShareReason: ``,
      sharingAccessType: SharingAccessType.None,
    };
  }

  // User is not given an editor access to share, but has superadmin or staff privilege
  if (isAllowedSuperadminOverride(entity)) {
    return {
      enableShare: true,
      requireSuperadminOverride: true,
      disabledShareReason: ``,
      sharingAccessType: SharingAccessType.SuperAdmin,
    };
  }

  if (!(entity.canShare || entity.can_share) || !(entity.canUpdateBasic || entity.can_update_basic)) {
    const canShare = entity.canShare || entity.can_share;
    const canUpdateBasic = entity.canUpdateBasic || entity.can_update_basic;
    // TODO(i18n): Translate this. It's currently used in many places where i18n context still needs to be
    // propagated through.
    const entityTypeNamePlural = EntityTypeNamePlural[EntityTypeKey[entityType]];
    let disabledShareReason = ``;
    let sharingAccessType = SharingAccessType.None;
    // Consumers can update their reports but cannot share
    if (!canShare && canUpdateBasic) {
      disabledShareReason = `Due to your project role(Consumer), your ${entityTypeNamePlural} are always hidden and cannot be shared with others`;
    } else {
      disabledShareReason = `You have view-only access and cannot share`;
      sharingAccessType = SharingAccessType.Viewer;
    }
    return {
      enableShare: false,
      requireSuperadminOverride: false,
      disabledShareReason,
      sharingAccessType,
    };
  }

  return {
    enableShare: true,
    requireSuperadminOverride: false,
    disabledShareReason: ``,
    sharingAccessType: SharingAccessType.Editor,
  };
}

/** is the user staff without membership to the project */
export function allowStaffOverride(projectId: number | string): boolean {
  const {isStaff, projects} = userDetailsStore.value;

  return isStaff && !projects[projectId];
}

export function getGlobalAccessTypeParam(globalAccessType: GlobalAccessType): string {
  switch (globalAccessType) {
    case GlobalAccessType.OFF:
      return `off`;
    case GlobalAccessType.VIEWER:
      return `viewer`;
    case GlobalAccessType.EDITOR:
      return `editor`;
    default:
      return `off`;
  }
}

/** Add global_access_type param to params for CREATE APIs.*/
export function maybeAddGlobalAccessTypeParam(params, saveAsNew, globalAccessType) {
  return {
    ...params,
    ...(saveAsNew ? {[GlobalAccessParamName]: getGlobalAccessTypeParam(globalAccessType || GlobalAccessType.OFF)} : {}),
  };
}
